import Grid from '@mui/material/Grid2';
import { Box, Button, Card, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { ReactNode, useRef, useMemo } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useQueryClient } from '@tanstack/react-query';

import { useLocalization } from 'localization';
import ContinueAndPreviewContainer from 'components/v2/container/ContinueAndPreviewContainer';
import DownloadIcon from 'components/v2/icons/DownloadIcon';
import { colors } from 'theme/mui/colors';
import Chip from 'components/v2/chip/Chip';
import { MODALS_STORE, TASKS_MODAL_ID } from 'constants/ui/modal';
import { getOrderTasksApi, ordersQueryKey } from 'api/orders/query';
import { IApiResponse, TaskCategory, TaskStatus } from 'api';
import { getProposalApi } from 'api/bookings/query';
import { usePropertyTypes } from 'modules/orders/hooks';
import { userStore } from 'stores/userStore';
import useOpenTasksModal from 'hooks/services/useOpenTasksModal';
import { createNewOrderStore } from 'stores/createNewOrderStore';
import NewOrderReceipt from '../receipt/NewOrderReceipt';
import NewPreviewReceipt from '../receipt/NewPreviewReceipt';
import useNewOrderCalculatePrice from 'hooks/general/useNewOrderCalculatePrice';

const CreateOrderStep5 = () => {
  const { t } = useLocalization();
  const { user } = userStore();
  const { formData } = createNewOrderStore();
  const queryClient = useQueryClient();
  const response = queryClient.getQueryData<IApiResponse>(ordersQueryKey.createOrderResponse);
  const openTask = useOpenTasksModal();

  const printRef = useRef<HTMLDivElement>(null);
  const { toggleModal } = MODALS_STORE.tasks();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Order_Receipt_${formData?.orderId}`,
  });

  const { data } = getOrderTasksApi(response?.data?.orderId ?? formData?.orderId);
  const findBooking = data?.find(({ type }) => type === TaskCategory.BookPhotographer);

  const {
    data: proposalData,
    isLoading: isProposalLoading,
    isFetching,
  } = getProposalApi(response?.data?.orderId ?? formData?.orderId ?? '');

  const { propertyTypeOptions } = usePropertyTypes();
  //constants to render UI
  const currentDepartment = user?.branches?.find(branch => branch.id === formData?.case?.departmentId);
  const propertyType = propertyTypeOptions.find(property => property.value === formData?.property.type);

  const totalPrice = useNewOrderCalculatePrice();
  const renderBookingComplete = () => (
    <Box>
      <Chip label={t('orders.pages.new.receipt.bookingSelected')} />
    </Box>
  );

  const renderNoProposalData = () => (
    <Card sx={{ my: '1rem !important' }}>
      <Typography variant="body2">{t('modals.bookPhotographer.cannotBook')}</Typography>
    </Card>
  );

  const mappingComponents = (): ReactNode => {
    if (isProposalLoading || isFetching) return <Skeleton />;
    if (findBooking?.status === TaskStatus.Complete || findBooking?.status === TaskStatus.Partial)
      return renderBookingComplete();
    if (!Object.keys(proposalData ?? {}).length) return renderNoProposalData();

    return (
      <Card sx={{ my: '1rem !important' }}>
        <Grid
          container
          sx={{
            gap: {
              xs: '0.5rem',
              lg: 'unset',
            },
          }}
        >
          <Grid size={{ xs: 12, lg: 2 }}>
            <Stack
              sx={{
                alignItems: {
                  xs: 'center',
                },
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <img alt="icon" src="/img/camera.png" style={{ width: '6.125rem', height: '4.375rem' }} />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Stack
              sx={{
                display: {
                  sx: 'flex',
                  lg: 'block',
                },
                mb: 1,
              }}
            >
              <Chip label="Save time" />
            </Stack>
            <Stack gap={0.25}>
              <Typography variant="h5">{t('orders.texts.photographyAppointment')}</Typography>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: '90%',
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              >
                {t('orders.texts.bookingReminder')}
              </Typography>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Stack justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() =>
                  toggleModal(TASKS_MODAL_ID.BOOK_PHOTOGRAPHER, findBooking ? { task: findBooking } : undefined)
                }
              >
                {t('common.terms.bookNow')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const filterTasks = useMemo(() => data?.filter(task => task.status !== TaskStatus.Complete) ?? [], [data]);

  return (
    <Stack direction="row" justifyContent="space-between" sx={{ height: '100%' }}>
      <Stack gap={1} sx={{ width: '100%', padding: '1rem 1rem 1rem 0', overflowY: 'auto' }}>
        <Stack spacing={1.25} mb="0.75rem" mt="0.75rem">
          <Typography variant="h3">{t('orders.steps.title.orderCompleted')}</Typography>
          <Typography variant="body1" sx={{ color: colors.gray[500] }}>
            {t('orders.pages.new.receipt.thanksMessage')}
          </Typography>
        </Stack>
        {/* rendering booking photographers conditions */}
        {mappingComponents()}
        <Card sx={{ margin: '0 !important', mb: '1.5rem !important', mt: '1.5rem !important' }}>
          <Typography variant="h5" fontWeight={600}>
            {t('orders.pages.new.receipt.tasksTitle')}
          </Typography>
          {filterTasks?.length > 0 ? (
            <>
              <Typography variant="body2">{t('orders.pages.new.receipt.tasksInstructions')}</Typography>
              <Grid container spacing={1.25}>
                {filterTasks.map((task, index) => {
                  return (
                    <Grid key={`${task.id}${index}`} size={{ xs: 12 }}>
                      <Card>
                        <Stack
                          sx={{
                            flexDirection: {
                              xs: 'column',
                              md: 'row',
                            },
                            gap: 1,
                          }}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography>{`${t(`tasks.categories.${task.type}`)} - (${t('common.terms.product')}: ${
                            task.productName
                          })`}</Typography>
                          <Button onClick={() => openTask(task)} variant="contained">
                            {t('common.actions.openTask')}
                          </Button>
                        </Stack>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          ) : (
            <Typography variant="body2">{t('orders.pages.new.receipt.noTasksInstructions')}</Typography>
          )}
        </Card>

        {/* Order Information */}
        <Card
          sx={{
            p: { xs: 2.5, md: 3.5 },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
            <Typography variant="h4">{t('orders.title.orderInformation')}</Typography>
          </Stack>
          <Card
            sx={{
              flexDirection: 'row',
              background: colors.gray[200],
              mb: 3,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              {`${t('common.terms.case')}: `}
            </Typography>
            <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
              {formData?.case?.reference}
            </Typography>
          </Card>
          <Grid container columnSpacing={{ xs: 0, md: 4 }} rowSpacing={3}>
            {/* {property type} */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h5" mb={2} fontWeight={700}>
                {t('orders.title.propertyInformation')}
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {t('common.terms.address')}
              </Typography>
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                  {`${formData?.address?.houseNumber} ${formData?.address?.street} ${formData?.address?.full ?? ''}`}
                </Typography>
              </Stack>
              <Divider sx={{ my: 1.75 }} />
              <Typography variant="subtitle2" fontWeight={700}>
                {t('common.terms.propertyType')}
              </Typography>
              <Typography variant="body2">{propertyType?.label ?? '-'}</Typography>
              <Divider sx={{ my: 1.75 }} />
              {/* keypickup */}
              <Typography variant="subtitle2" fontWeight={700}>
                {t('common.terms.keyPickupAddress')}
              </Typography>
              <Typography variant="body2">{formData?.pickUpKeyAddress?.full ?? '-'}</Typography>
              {!!formData?.hasExternalStylist && (
                <>
                  <Divider sx={{ my: 1.75 }} />
                  {/* externalStylist */}
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('common.terms.keyPickupAddress')}
                  </Typography>
                  <Typography variant="body2">{formData?.pickUpKeyAddress?.full ?? '-'}</Typography>
                </>
              )}
            </Grid>
            {/* Agent information */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h5" fontWeight={700} mb={2}>
                {t('orders.pages.new.stepFour.agentInformationCardTitle')}
              </Typography>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {t('common.terms.department')}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {currentDepartment?.name ?? '-'}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {t('common.terms.agent')}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {user?.fullName ?? '-'}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                <Stack>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('common.terms.agentPhone')}
                  </Typography>
                  <Typography variant="body2">{user?.phoneNumber ?? '-'}</Typography>
                </Stack>
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                <Stack>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('common.terms.agentEmail')}
                  </Typography>
                  <Typography variant="body2">{user?.email ?? '-'}</Typography>
                </Stack>
              </Stack>
            </Grid>
            {/* Contact Information */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="h5" fontWeight={700} mb={2}>
                {t('common.terms.contactInformation')}
              </Typography>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {t('orders.contactPersonTypes.OWNER')}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {formData?.contactPersons?.[0]?.name || '-'}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                <Stack>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('common.terms.phoneContact')}
                  </Typography>
                  <Typography variant="body2">
                    {formData?.contactPersons?.[0]?.phone || formData?.contactPersons?.[0]?.phoneAlt || '-'}
                  </Typography>
                </Stack>
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                <Stack>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('common.terms.emailContact')}
                  </Typography>
                  <Typography variant="body2">{formData?.contactPersons?.[0]?.email || '-'}</Typography>
                </Stack>
              </Stack>
              <Divider sx={{ my: 1.75 }} />
              {/* {photographer comment goes here} */}
              <Typography variant="subtitle2" fontWeight={700}>
                {t('common.terms.photographerComment')}
              </Typography>
              <Typography variant="body2">{formData?.photographerComment || '-'}</Typography>
            </Grid>
          </Grid>
        </Card>
      </Stack>
      <Stack sx={{ display: 'none' }}>
        <NewOrderReceipt ref={printRef} />
      </Stack>
      <ContinueAndPreviewContainer
        continueElement={
          <Stack gap={2}>
            {user?.visiblePrices && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">{t('common.terms.totalPrice')}</Typography>
                <Typography variant="h5" fontWeight={600}>
                  {totalPrice}
                </Typography>
              </Stack>
            )}
            <Button size="large" variant="outlined" onClick={() => handlePrint()} fullWidth endIcon={<DownloadIcon />}>
              {t('common.actions.downloadReceipt')}
            </Button>
          </Stack>
        }
        previewElement={<NewPreviewReceipt />}
      />
    </Stack>
  );
};

export default CreateOrderStep5;
